import { fetchConfig } from '@theme/utilities';

/**
 * "Recommended for you" carousel inside the cart drawer.
 * Handles arrow scrolling, revealing size pills, and adding a variant to the cart.
 * Uses event delegation so it keeps working after the drawer is morphed on cart updates.
 *
 * Works on Horizon 3.x (CartAddEvent from @theme/events) and Horizon 4.x
 * (CartLinesUpdateEvent from @shopify/events): whichever the theme's import map provides.
 */
const cartEvents = import('@shopify/events')
  .then(({ CartLinesUpdateEvent }) => ({ CartLinesUpdateEvent }))
  .catch(() => import('@theme/events').then(({ CartAddEvent }) => ({ CartAddEvent })));

class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#track?.addEventListener('scroll', this.#updateArrows, { passive: true });
    requestAnimationFrame(this.#updateArrows);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#track?.removeEventListener('scroll', this.#updateArrows);
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-rec-track]'));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const arrow = target.closest('[data-rec-scroll]');
    if (arrow instanceof HTMLElement) return this.#scroll(Number(arrow.dataset.recScroll));

    const toggle = target.closest('[data-rec-toggle]');
    if (toggle instanceof HTMLElement) return this.#toggleOptions(toggle);

    const add = target.closest('[data-rec-add]');
    if (add instanceof HTMLButtonElement && !add.disabled) this.#addToCart(add);
  };

  /** @param {number} direction */
  #scroll(direction) {
    const track = this.#track;
    const card = track?.firstElementChild;
    if (!track || !(card instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({ left: direction * (card.offsetWidth + gap) });
  }

  #updateArrows = () => {
    const track = this.#track;
    if (!track) return;

    const [prev, next] = this.querySelectorAll('[data-rec-scroll]');
    const maxScroll = track.scrollWidth - track.clientWidth - 1;
    if (prev instanceof HTMLButtonElement) prev.disabled = track.scrollLeft <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = track.scrollLeft >= maxScroll;
  };

  /** @param {HTMLElement} toggle */
  #toggleOptions(toggle) {
    const options = document.getElementById(toggle.getAttribute('aria-controls') || '');
    if (!options) return;

    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    options.hidden = expanded;
  }

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    const card = button.closest('.cart-rec');
    const error = card?.querySelector('.cart-rec__error');
    const variantId = button.dataset.recAdd || '';
    const productId = card instanceof HTMLElement ? card.dataset.productId : undefined;
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    card?.setAttribute('aria-busy', 'true');
    if (error) error.textContent = '';

    const { CartLinesUpdateEvent, CartAddEvent } = await cartEvents;

    // Horizon 4: announce the add up front with a promise the drawer, bubble and cart items wait on.
    const deferred = CartLinesUpdateEvent?.createPromise();
    if (CartLinesUpdateEvent && deferred) {
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: deferred.promise,
        })
      );
    }

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sectionIds.join(','),
          }),
        })
      );
      const data = await response.json();
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
      const detail = {
        source: 'cart-recommendations',
        items: cart.items,
        itemCount: cart.item_count,
        productId,
        variantId,
        sections: data.sections,
        didError: Boolean(data.status),
      };

      if (data.status && error) error.textContent = data.description || data.message;

      if (deferred) {
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail });
      } else if (CartAddEvent && !data.status) {
        this.dispatchEvent(new CartAddEvent(cart, variantId, detail));
      }
    } catch (err) {
      console.error(err);
      deferred?.reject(err);
    } finally {
      card?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
